/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-redundant-type-constituents */
import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAppSelector, useAppDispatch } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { setLiveStatus } from '../../../redux/features/streaming/streamingSlice';
import { ThinkingIndicator } from '../shared/ThinkingIndicator';
import { customTokens } from '../../../styles';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '75%',
        ...shorthands.gap(customTokens.spacingHorizontalS),
    },
    bubble: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.padding(customTokens.spacingVerticalM),
        borderRadius: '2px 20px 20px 20px',
        gap: '10px',
        color: tokens.colorNeutralForeground1,
        maxWidth: '100%',
        minWidth: 0,
    },
    content: {
        wordBreak: 'break-word',
        '& p': {
            margin: '0',
        },
    },
    cursor: {
        display: 'inline-block',
        width: '2px',
        height: '1em',
        backgroundColor: tokens.colorNeutralForeground1,
        marginLeft: '2px',
        verticalAlign: 'text-bottom',
        animation: 'cursorBlink 1s step-end infinite',
    },
});

interface StreamingMessageBubbleProps {
    chatId: string;
}

/**
 * Single-line progress sentence shown next to the waiting dots.
 * - Reveals word-by-word (like streaming) when a new label appears.
 * - Each word runs a looping shimmer; per-word stagger makes a glow sweep from the start
 *   of the sentence to the end (heartbeat-like).
 * Re-mounts whenever the label changes (via `key`), which restarts the reveal.
 */
const SHIMMER_CYCLE_SEC = 1.2;
const CHAR_REVEAL_MS = 25;

const StatusLine: React.FC<{ label: string }> = ({ label }) => {
    const chars = useMemo(() => label.split(''), [label]);
    const [visible, setVisible] = useState(0);

    // Letter-by-letter reveal.
    useEffect(() => {
        setVisible(0);
        if (chars.length === 0) return;
        let i = 0;
        const id = setInterval(() => {
            i += 1;
            setVisible(i);
            if (i >= chars.length) clearInterval(id);
        }, CHAR_REVEAL_MS);
        return () => { clearInterval(id); };
    }, [chars]);

    return (
        <span style={{ fontSize: '13px', display: 'inline', whiteSpace: 'pre-wrap' }}>
            {chars.map((c, i) => (
                <span
                    key={i}
                    className={c !== ' ' ? 'status-shimmer-word' : undefined}
                    style={{
                        display: 'inline-block',
                        opacity: i < visible ? 1 : 0,
                        transform: i < visible ? 'translateY(0)' : 'translateY(2px)',
                        transition: `opacity 0.12s ease, transform 0.12s ease`,
                        animationDuration: `${SHIMMER_CYCLE_SEC}s`,
                        animationDelay: `${(i / Math.max(chars.length, 1)) * SHIMMER_CYCLE_SEC}s`,
                        width: c === ' ' ? '0.3em' : undefined,
                    }}
                >
                    {c === ' ' ? ' ' : c}
                </span>
            ))}
        </span>
    );
};

export const StreamingMessageBubble: React.FC<StreamingMessageBubbleProps> = ({ chatId }) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const streaming = useAppSelector((state: RootState) => state.streaming);
    const streamState = streaming[chatId] as typeof streaming[string] | undefined;

    const superAgentBuffer = streamState?.superAgentBuffer;
    const markdownContent = useMemo(() => superAgentBuffer ?? '', [superAgentBuffer]);

    const isStreamingActive = streamState?.isStreaming ?? false;
    const panelCount = streamState?.agentPanels.length ?? 0;
    const transitionPhase = streamState?.transitionPhase ?? 'idle';

    // GAP 2: Routing LLM kararı sessiz geçiyor. Henüz hiç ajan paneli yoksa,
    // 3.5s sonra bir ek mesaj göster; 7s'de daha uzun bir uyarı.
    useEffect(() => {
        if (!isStreamingActive || panelCount > 0) return;
        const t1 = setTimeout(() => {
            dispatch(setLiveStatus({ chatId, label: 'Yanıt için en doğru birimi belirliyorum...' }));
        }, 3500);
        const t2 = setTimeout(() => {
            dispatch(setLiveStatus({ chatId, label: 'Sorunuz özenle değerlendiriliyor, lütfen bekleyin...' }));
        }, 7000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [isStreamingActive, panelCount, chatId, dispatch]);

    // GAP 5: Tüm ajan panelleri tamamlandı, SuperAgent sentezi başlamadı.
    // 2.5s sonra ve 5s sonra ek mesajlar göster.
    useEffect(() => {
        if (transitionPhase !== 'transition') return;
        const t1 = setTimeout(() => {
            dispatch(setLiveStatus({ chatId, label: 'Son rötuşlar yapılıyor...' }));
        }, 2500);
        const t2 = setTimeout(() => {
            dispatch(setLiveStatus({ chatId, label: 'Uzun bir yanıt hazırlanıyor, lütfen bekleyin...' }));
        }, 5000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [transitionPhase, chatId, dispatch]);

    if (streamState == null || (!streamState.isStreaming && !streamState.superAgentBuffer)) {
        return null;
    }

    const showContent = streamState.superAgentBuffer.length > 0;
    const isStreaming = streamState.isStreaming;
    const isWaiting = isStreaming && !showContent;

    return (
        <div className={classes.root}>
            <style>{`
                @keyframes cursorBlink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                }
                @keyframes dotBounce {
                    0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                    40% { transform: translateY(-5px); opacity: 1; }
                }
                @keyframes statusFade {
                    from { opacity: 0; transform: translateY(2px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes statusWordGlow {
                    0%, 100% { color: #888888; text-shadow: none; }
                    50% { color: #0078d4; text-shadow: 0 0 8px rgba(0, 120, 212, 0.5); }
                }
                .status-shimmer-word {
                    animation-name: statusWordGlow;
                    animation-timing-function: ease-in-out;
                    animation-iteration-count: infinite;
                }
            `}</style>
            <ThinkingIndicator chatId={chatId} />
            {isWaiting && (
                <div className={classes.bubble} style={{ width: 'fit-content' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 2px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                            {[0, 1, 2].map((i) => (
                                <span
                                    key={i}
                                    style={{
                                        display: 'inline-block',
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: '#0078d4',
                                        animation: `dotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                                    }}
                                />
                            ))}
                        </div>
                        {streamState.liveStatus && (
                            <StatusLine key={streamState.liveStatus} label={streamState.liveStatus} />
                        )}
                    </div>
                </div>
            )}
            {showContent && (
                <div className={classes.bubble}>
                    <div className={classes.content}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownContent}</ReactMarkdown>
                        {isStreaming && <span className={classes.cursor} />}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StreamingMessageBubble;
